import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle, Sparkles,
  Bird, Mail, Phone, Building2, User, Clock, ChevronRight, FileDown
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';
import BrandLogo from '../components/BrandLogo';
import { UpgradeSection, StickyUpgradeBar, TeamTrainingLink } from '../components/disc/UpgradeOffer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand palette per spec
const COLORS = {
  navy: '#3D4F7C',
  teal: '#4FB6C9',
  orange: '#E16B3F',
  yellow: '#F2C14E',
};

// Bird mascot artwork (kept in sync with backend disc_bird_styles.BIRD_IMAGES)
const BIRD_IMAGES = {
  Eagle:  '/brand/eagle.png',
  Parrot: '/brand/parrot.png',
  Dove:   '/brand/dove.png',
  Owl:    '/brand/owl.png',
};

// Bird visual metadata
const BIRD_META = {
  Eagle:  { color: COLORS.orange, gradient: 'from-orange-500 to-amber-500', image: BIRD_IMAGES.Eagle,  letter: 'D', label: 'Dominant' },
  Parrot: { color: COLORS.yellow, gradient: 'from-yellow-400 to-orange-400', image: BIRD_IMAGES.Parrot, letter: 'I', label: 'Interactive' },
  Dove:   { color: COLORS.teal,   gradient: 'from-teal-400 to-cyan-500',    image: BIRD_IMAGES.Dove,   letter: 'S', label: 'Supportive' },
  Owl:    { color: COLORS.navy,   gradient: 'from-indigo-700 to-slate-700', image: BIRD_IMAGES.Owl,    letter: 'C', label: 'Conscientious' },
};

// Stages: 'intro' → quiz steps 0-9 → 'lead' → 'result'
export default function FindYourBird() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('intro'); // intro | quiz | lead | result
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    organization: '', role: '', marketing_consent: false,
  });
  const [result, setResult] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);

  // SEO
  useEffect(() => {
    document.title = 'Find Your DISC Bird | Culture Align';
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', name); document.head.appendChild(m); }
      m.setAttribute('content', content);
    };
    setMeta('description', 'Discover how you naturally communicate, collaborate, and show up in a team. Take the free 3-minute Find Your DISC Bird quiz from Culture Align by The WE Mentality.');
  }, []);

  // Track landing (questions load only after registration)
  useEffect(() => {
    axios.post(`${API}/analytics/event`, { type: 'disc_quiz_landed', meta: {} }).catch(() => {});
  }, []);

  const progress = useMemo(() => {
    if (stage === 'intro') return 0;
    if (stage === 'result') return 100;
    if (stage === 'lead') return 5;
    const answered = answers.filter((a) => a !== null).length;
    return 5 + Math.round((answered / 10) * 90);
  }, [stage, answers]);

  const startQuiz = () => setStage('lead');

  const selectAnswer = (qIdx, choice, birdName) => {
    if (submitting) return;
    const next = [...answers];
    next[qIdx] = birdName;
    setAnswers(next);
    // Auto-advance; last answer submits
    setTimeout(() => {
      if (qIdx < 9) setStep(qIdx + 1);
      else submitQuiz(next);
    }, 220);
  };

  const goBack = () => {
    if (stage === 'lead') { setStage('intro'); return; }
    if (stage === 'quiz' && step > 0) setStep(step - 1);
    else if (stage === 'quiz' && step === 0) setStage('lead');
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.first_name.trim() || !lead.last_name.trim() || !lead.email.trim()) {
      toast.error('Please provide first name, last name, and email.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/disc-quiz/register`, { ...lead, source_page: '/find-your-bird' });
      const q = await axios.get(`${API}/disc-quiz/questions`, { params: { registration_id: data.registration_id } });
      setRegistrationId(data.registration_id);
      setQuestions(q.data.questions || []);
      setAnswers(Array(10).fill(null));
      setStep(0);
      setStage('quiz');
      axios.post(`${API}/analytics/event`, { type: 'disc_quiz_started', meta: {} }).catch(() => {});
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? 'Please enter a valid first name, last name, and email.' : (d || 'Registration failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    if (finalAnswers.some((a) => a === null)) {
      toast.error('Please answer all questions before submitting.');
      setStep(finalAnswers.findIndex((a) => a === null));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/disc-quiz/submit`, {
        registration_id: registrationId,
        answers: finalAnswers,
        source_page: '/find-your-bird',
      });
      setResult(data);
      setStage('result');
      axios.post(`${API}/analytics/event`, {
        type: 'disc_quiz_completed',
        meta: { primary: data.primary_style, secondary: data.secondary_style, tied: data.tied },
      }).catch(() => {});
    } catch (err) {
      console.error('[DISC] submit failed:', err?.response?.data || err?.message);
      toast.error('Submission failed: ' + (err?.response?.data?.detail || err?.message));
      if (err?.response?.status === 403) setStage('lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 text-slate-900">
      {/* ── HEADER ───────────────────────────────────── */}
      <header className="py-5 px-4 sm:px-6 lg:px-8 border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="fyb-logo-link">
            <BrandLogo
              className="h-9 sm:h-11 w-auto"
              fallbackClassName="text-base font-bold tracking-wide text-slate-900 whitespace-nowrap"
              testId="fyb-header-logo-img"
            />
          </a>
          {stage !== 'intro' && stage !== 'result' && (
            <button
              onClick={goBack}
              className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="fyb-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
        </div>
        {/* Progress bar */}
        {stage !== 'intro' && (
          <div className="max-w-6xl mx-auto mt-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span data-testid="fyb-progress-label">
                {stage === 'lead' ? 'Register to begin your free quiz' :
                 stage === 'result' ? 'Complete' :
                 submitting ? 'Calculating your result…' :
                 `Question ${step + 1} of 10`}
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${COLORS.navy}, ${COLORS.teal})` }}
                data-testid="fyb-progress-bar"
              />
            </div>
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        {/* ── INTRO ───────────────────────────────────── */}
        {stage === 'intro' && (
          <section className="text-center" data-testid="fyb-intro">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Free Mini Assessment
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5" style={{ color: COLORS.navy }}>
              Find Your <span style={{ color: COLORS.teal }}>DISC Bird</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-3">
              Discover how you naturally communicate, collaborate, and show up in a team.
            </p>
            <p className="text-sm sm:text-base text-slate-500 max-w-2xl mx-auto mb-10">
              This quick self-reflection tool gives you a snapshot of the DISC Bird style you may connect with most.
              It is <strong>not a full DISC assessment</strong>, but it will help you begin building self-awareness and stronger communication.
            </p>

            {/* Bird preview cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto mb-10">
              {Object.entries(BIRD_META).map(([name, meta]) => (
                <div
                  key={name}
                  className={`p-5 rounded-2xl bg-gradient-to-br ${meta.gradient} text-white shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center`}
                  data-testid={`fyb-bird-card-${name.toLowerCase()}`}
                >
                  <img
                    src={meta.image}
                    alt={`${name} mascot`}
                    className="h-20 sm:h-24 w-auto object-contain mb-3 drop-shadow-md"
                    decoding="async"
                  />
                  <p className="text-xs font-bold uppercase tracking-wider opacity-90">{meta.letter} · {meta.label}</p>
                  <p className="text-lg font-bold">{name}</p>
                </div>
              ))}
            </div>

            <Button
              onClick={startQuiz}
              size="lg"
              className="text-white font-bold px-10 py-7 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all"
              style={{ background: `linear-gradient(90deg, ${COLORS.navy}, ${COLORS.teal})` }}
              data-testid="fyb-start-quiz-btn"
            >
              Start the Quiz <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-slate-500 inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Takes less than 3 minutes
            </p>
          </section>
        )}

        {/* ── QUIZ ───────────────────────────────────── */}
        {stage === 'quiz' && questions[step] && (
          <section data-testid={`fyb-quiz-q${step}`}>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: COLORS.teal }}>
              Question {step + 1} of 10
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 leading-tight" style={{ color: COLORS.navy }}>
              {questions[step].question}
            </h2>
            <div className="space-y-3">
              {questions[step].options.map((opt, idx) => {
                const selected = answers[step] === opt.bird;
                return (
                  <button
                    key={opt.label || idx}
                    onClick={() => selectAnswer(step, idx, opt.bird)}
                    className={`w-full text-left p-5 sm:p-6 rounded-2xl border-2 transition-all duration-200 ${
                      selected
                        ? 'border-teal-500 bg-teal-50 shadow-lg scale-[1.01]'
                        : 'border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/40 hover:shadow-md'
                    }`}
                    data-testid={`fyb-answer-${step}-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-colors ${
                        selected ? 'border-teal-500 bg-teal-500 text-white' : 'border-slate-300 text-slate-500'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <span className="text-base sm:text-lg text-slate-800 leading-snug">{opt.label}</span>
                      {selected && <CheckCircle className="ml-auto h-5 w-5 text-teal-500 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── LEAD CAPTURE ───────────────────────────────────── */}
        {stage === 'lead' && (
          <section data-testid="fyb-lead-form">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="h-3.5 w-3.5" /> Free · About 3 Minutes
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: COLORS.navy }}>
                Register to start your DISC Bird quiz
              </h2>
              <p className="text-slate-600 max-w-xl mx-auto">
                Enter your details to begin. Your result will appear on screen and be emailed to you.
              </p>
            </div>

            <form onSubmit={submitLead} className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField label="First Name *" icon={User}>
                  <Input
                    value={lead.first_name}
                    onChange={(e) => setLead({ ...lead, first_name: e.target.value })}
                    required
                    className="h-11"
                    data-testid="fyb-input-first-name"
                  />
                </FormField>
                <FormField label="Last Name *" icon={User}>
                  <Input
                    value={lead.last_name}
                    onChange={(e) => setLead({ ...lead, last_name: e.target.value })}
                    required
                    className="h-11"
                    data-testid="fyb-input-last-name"
                  />
                </FormField>
              </div>
              <FormField label="Email *" icon={Mail}>
                <Input
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  required
                  className="h-11"
                  data-testid="fyb-input-email"
                />
              </FormField>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField label="Phone (optional)" icon={Phone}>
                  <Input
                    value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    className="h-11"
                    data-testid="fyb-input-phone"
                  />
                </FormField>
                <FormField label="Role (optional)" icon={User}>
                  <Input
                    value={lead.role}
                    onChange={(e) => setLead({ ...lead, role: e.target.value })}
                    placeholder="Educator, Manager, Founder…"
                    className="h-11"
                    data-testid="fyb-input-role"
                  />
                </FormField>
              </div>
              <FormField label="Organization (optional)" icon={Building2}>
                <Input
                  value={lead.organization}
                  onChange={(e) => setLead({ ...lead, organization: e.target.value })}
                  className="h-11"
                  data-testid="fyb-input-organization"
                />
              </FormField>

              <div className="flex items-start gap-3 pt-2">
                <Checkbox
                  id="fyb-consent"
                  checked={lead.marketing_consent}
                  onCheckedChange={(v) => setLead({ ...lead, marketing_consent: !!v })}
                  data-testid="fyb-consent"
                />
                <label htmlFor="fyb-consent" className="text-sm text-slate-600 cursor-pointer">
                  <span className="font-medium text-slate-700">Optional:</span> Yes, send me emails from The WE Mentality about leadership development, DISC assessments, team training, workshops, and strategic planning services. You can unsubscribe at any time. Your quiz results are emailed to you either way.
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full text-white font-bold py-7 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all"
                style={{ background: `linear-gradient(90deg, ${COLORS.navy}, ${COLORS.teal})` }}
                data-testid="fyb-submit-btn"
              >
                {submitting ? 'Starting…' : <>Start the Quiz <ArrowRight className="ml-2 h-5 w-5" /></>}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Your info is private. We won't share it with third parties.
              </p>
            </form>
          </section>
        )}

        {/* ── RESULT ───────────────────────────────────── */}
        {stage === 'result' && result && <ResultView result={result} />}
      </main>

      <footer className="border-t border-slate-200 py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <span>© {new Date().getFullYear()} {companyInfo.name}</span>
          <div className="flex items-center gap-5">
            <a href="/" className="hover:text-slate-900">Home</a>
            <a href="/workshops" className="hover:text-slate-900">Workshops</a>
            <a href="/case-studies" className="hover:text-slate-900">Case Studies</a>
            <a href="/assessment" className="hover:text-slate-900">WE Alignment Index</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function FormField({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-slate-700 text-sm font-medium mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />} {label}
      </Label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function ResultView({ result }) {
  const bird = result.bird || {};
  const meta = BIRD_META[result.primary_style] || BIRD_META.Eagle;
  const sortedScores = Object.entries(result.scores || {}).sort((a, b) => b[1] - a[1]);
  const upgradeRef = useRef(null);

  return (
    <section className="pb-20 sm:pb-0" data-testid="fyb-result">
      {/* Success banner */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold uppercase tracking-wider mb-4">
          <CheckCircle className="h-3.5 w-3.5" /> Result Ready
        </div>
        <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Your DISC Bird result is ready. This is just the beginning — use it to build self-awareness,
          improve communication, and better understand how you show up with others.
        </p>
        {result.email_sent && (
          <p className="mt-3 text-sm text-slate-500 inline-flex items-center gap-1.5" data-testid="fyb-email-sent-note">
            <Mail className="h-3.5 w-3.5" /> A copy of your result has been emailed to you.
          </p>
        )}
      </div>

      {/* Primary result card */}
      <div
        className={`relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br ${meta.gradient} text-white p-8 sm:p-12 mb-6`}
        data-testid="fyb-result-card"
      >
        <img
          src={meta.image}
          alt=""
          aria-hidden="true"
          className="absolute -top-6 -right-6 sm:-top-8 sm:-right-8 h-48 sm:h-64 w-auto object-contain opacity-25 select-none pointer-events-none drop-shadow-2xl"
          decoding="async"
        />
        <div className="relative z-10">
          <img
            src={meta.image}
            alt={`${result.primary_style} mascot`}
            className="h-24 sm:h-32 w-auto object-contain mb-5 drop-shadow-xl"
            decoding="async"
            data-testid="fyb-result-hero-image"
          />
          <p className="text-xs font-bold uppercase tracking-widest opacity-90">{bird.letter} · {bird.label}</p>
          <h1 className="text-5xl sm:text-6xl font-extrabold mt-1 mb-3" data-testid="fyb-result-name">
            {result.primary_style}
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-5">{bird.headline}</p>
          <p className="text-base sm:text-lg leading-relaxed opacity-95 max-w-2xl">
            {bird.description}
          </p>
          {result.tied && (
            <div className="mt-5 inline-block bg-white/20 backdrop-blur rounded-xl px-4 py-2.5 text-sm">
              <strong>Note:</strong> {result.blended_message}
            </div>
          )}
        </div>
      </div>

      <UpgradeSection result={result} sectionRef={upgradeRef} />
      <StickyUpgradeBar result={result} sectionRef={upgradeRef} />

      {/* Detail cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <DetailCard color={meta.color} label="Communication" body={bird.communication} />
        <DetailCard color={meta.color} label="Team Strength" body={bird.team_strength} />
        <DetailCard color={meta.color} label="Possible Blind Spot" body={bird.blind_spot} />
      </div>

      {/* Deeper insights row */}
      <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="fyb-insights">
        <InsightCard
          color={meta.color}
          label="Stress Behaviors"
          body={bird.stress_behaviors}
          icon="⚠️"
        />
        <InsightCard
          color={meta.color}
          label="Ideal Environment"
          body={bird.ideal_environment}
          icon="🌱"
        />
        <InsightCard
          color={meta.color}
          label="Team Dynamics"
          body={bird.team_dynamics}
          icon="🤝"
        />
        <InsightCard
          color={meta.color}
          label="Leadership Tendencies"
          body={bird.leadership_tendencies}
          icon="🧭"
        />
      </div>

      {/* Communication tips list */}
      {Array.isArray(bird.communication_tips) && bird.communication_tips.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 mb-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: meta.color }}>
            Communication Tips for {result.primary_style}s
          </p>
          <ul className="space-y-2.5">
            {bird.communication_tips.map((tip) => (
              <li key={tip} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-700">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: meta.color }} />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* How to communicate WITH this style */}
      {bird.how_to_communicate_with && (
        <div className="rounded-2xl p-6 sm:p-7 mb-8 border-l-4 bg-slate-50" style={{ borderColor: meta.color }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: meta.color }}>
            How to Communicate <em>With</em> a {result.primary_style}
          </p>
          <p className="text-base text-slate-700 leading-relaxed">{bird.how_to_communicate_with}</p>
        </div>
      )}

      {/* Growth step */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: meta.color }}>Growth Step</p>
        <p className="text-lg sm:text-xl text-slate-800 font-semibold leading-snug">{bird.growth_step}</p>
      </div>

      {/* Score breakdown */}
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-7 mb-10">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Your Style Mix</p>
        <div className="space-y-3" data-testid="fyb-score-breakdown">
          {sortedScores.map(([name, score]) => {
            const m = BIRD_META[name];
            const pct = Math.round((score / 10) * 100);
            return (
              <div key={name} className="flex items-center gap-4">
                <div className="flex items-center gap-2 w-28 sm:w-32 flex-shrink-0">
                  <img src={m.image} alt={`${name} mascot`} className="h-7 w-7 object-contain flex-shrink-0" decoding="async" />
                  <span className="font-semibold text-slate-800 text-sm">{name}</span>
                </div>
                <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: m.color }}
                  />
                </div>
                <span className="text-sm font-bold text-slate-700 w-10 text-right">{score}/10</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Download Bird Communication Guide PDF */}
      {result.lead_id && (
        <div className="mt-8 mb-2 rounded-2xl border-2 border-dashed p-6 sm:p-8 text-center" style={{ borderColor: COLORS.teal + '55', background: COLORS.teal + '0d' }} data-testid="fyb-pdf-section">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ background: COLORS.teal + '22' }}>
            <FileDown className="h-6 w-6" style={{ color: COLORS.teal }} />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: COLORS.navy }}>
            Download Your {result.primary_style} Communication Guide
          </h3>
          <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto mb-5">
            A two-page personalized PDF you can keep, share with your team, or bring to your next leadership conversation.
          </p>
          <a
            href={`${API}/disc-quiz/guide/${result.lead_id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              axios.post(`${API}/analytics/event`, {
                type: 'disc_quiz_pdf_downloaded',
                meta: { lead_id: result.lead_id, primary: result.primary_style },
              }).catch(() => {});
            }}
            className="inline-flex items-center gap-2 font-bold px-7 py-3.5 rounded-xl shadow-md hover:scale-105 transition-all text-white"
            style={{ background: COLORS.navy }}
            data-testid="fyb-pdf-download-btn"
          >
            <FileDown className="h-5 w-5" />
            Download Bird Communication Guide (PDF)
          </a>
        </div>
      )}

      <TeamTrainingLink result={result} />

      <div className="text-center mt-8 text-sm text-slate-500">
        <a href="/find-your-bird" onClick={(e) => { e.preventDefault(); window.location.reload(); }} className="hover:text-slate-900 underline-offset-2 hover:underline" data-testid="fyb-retake">
          Retake the quiz →
        </a>
      </div>
    </section>
  );
}

function DetailCard({ color, label, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color }}>{label}</p>
      <p className="text-slate-700 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function InsightCard({ color, label, body, icon }) {
  if (!body) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0">{icon}</div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color }}>{label}</p>
          <p className="text-slate-700 text-sm leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}
